import { ReactElement } from 'react';
import { PrintColumn, PrintDataRow, formatCellValue, getStandardCellValue } from './printDataTableUtils';

export function PrintDataTablePage({
  columns,
  rows,
  title,
  getCellValue,
}: {
  columns: PrintColumn[];
  rows: PrintDataRow[];
  title: string;
  getCellValue: (row: PrintDataRow, column: PrintColumn) => any;
}): ReactElement {
  return (
    <div className="print-data-table-page">
      <div className="print-section-header">{title}</div>
      <table className="print-data-table-content">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.internalName}
                className={col.dividerAfter ? 'print-divider-right' : ''}
                dangerouslySetInnerHTML={{ __html: col.displayName }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            const isConfig = row.type === 'config';
            const rowClass = isConfig ? 'print-config-row' : 'print-run-row';
            const key = isConfig ? `config-${row.config.id}-${rowIdx}` : `run-${row.run?.id || rowIdx}`;

            return (
              <tr key={key} className={rowClass}>
                {columns.map((col) => {
                  const value = getCellValue(row, col);
                  const rowTimezone = row.run?.timezone
                    ?? row.config?.runs?.find?.((r: any) => r?.timezone)?.timezone
                    ?? null;
                  const formatted = formatCellValue(
                    value, col.places, col.internalName, col.invert, rowTimezone, col.zeroIsMissing,
                  );
                  return (
                    <td
                      key={col.internalName}
                      className={col.dividerAfter ? 'print-divider-right' : ''}
                    >
                      {formatted}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Reusable PrintDataTable component that renders two pages of data
 */
export function PrintDataTable({
  rows,
  columnsPage1,
  columnsPage2,
  titlePage1,
  titlePage2,
  getCellValue = getStandardCellValue,
  className = 'print-only print-data-table',
}: {
  rows: PrintDataRow[];
  columnsPage1: PrintColumn[];
  columnsPage2: PrintColumn[];
  titlePage1: string;
  titlePage2: string;
  getCellValue?: (row: PrintDataRow, column: PrintColumn) => any;
  className?: string;
}): ReactElement {
  return (
    <div className={className}>
      {/* Page 1 */}
      <PrintDataTablePage
        columns={columnsPage1}
        rows={rows}
        title={titlePage1}
        getCellValue={getCellValue}
      />

      {/* Page 2 */}
      <div className="print-page-break-before">
        <PrintDataTablePage
          columns={columnsPage2}
          rows={rows}
          title={titlePage2}
          getCellValue={getCellValue}
        />
      </div>
    </div>
  );
}
